import { Tab } from '@headlessui/react';
import cn from 'classnames';
import Link from 'next/link';
import { FC, PropsWithChildren, memo } from 'react';
import { useTranslation as t } from '@utils/hooks';
import Divider from '../../../components/Divider';

type TabData = {
  title: string;
  slug: string;
  price: string;
  description: string;
  specs?: string[];
  popular?: boolean;
};

export type GroupedTab = {
  title: string;
  price: number;
  tabs: TabData[];
};

type PdpTierSelectorProps = {
  selectedPrimaryIndex: number;
  selectedSecondaryIndex: number;
  groupedTabs: GroupedTab[];
};

interface LinkToSubscriptionProps extends PropsWithChildren {
  selected: boolean;
  href: string;
  className: string;
}

const LinkToSubscription: FC<LinkToSubscriptionProps> = ({
  selected,
  href,
  className,
  children,
}) =>
  selected === false ? (
    <Link href={href} className={className} scroll={false}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  );

const PopularBadge: FC = () => (
  <p className="font-semibold text-xs px-2 py-1 rounded inline-block bg-nzxt-teal-100 dark:bg-nzxt-teal-300 text-nzxt-teal-500">
    Popular
  </p>
);

const PrimeBadge: FC = () => (
  <p className="font-medium text-xs px-2 py-1 rounded inline-block bg-nzxt-violet-500 dark:bg-nzxt-violet-300 text-white">
    Prime
  </p>
);

const PdpTierSelector: FC<PdpTierSelectorProps> = ({
  selectedPrimaryIndex,
  selectedSecondaryIndex,
  groupedTabs,
}) => {
  const CHOOSE_TEXT = t('subscription_pdp_choose_tier');

  const selectedPrice = groupedTabs[selectedPrimaryIndex]?.tabs?.find(
    (tab, idx) => idx === selectedSecondaryIndex
  )?.price;

  return (
    <div className="w-full">
      <div className="mt-8 text-base font-semibold">{CHOOSE_TEXT}</div>
      <Tab.Group selectedIndex={selectedPrimaryIndex}>
        <Tab.List className="flex gap-4 py-3">
          {groupedTabs.map((group, idx) => (
            <Tab
              key={group.title}
              as={LinkToSubscription}
              selected={idx === selectedPrimaryIndex}
              href={group.tabs[0].slug}
              className={({ selected }) =>
                cn(
                  'w-full h-full rounded-lg py-2 px-1 cursor-pointer border focus-visible:outline-none hover:shadow-activeBorder hover:border-transparent dark:hover:border-transparent transition',
                  {
                    'shadow-activeBorder border-transparent': selected,
                    'border-nzxt-light-grey-300 bg-nzxt-light-grey-50 dark:bg-nzxt-dark-grey-500':
                      !selected,
                  }
                )
              }
            >
              <div className="flex flex-col text-center items w-full gap-y-1">
                <div className="text-xs text-center font-semibold text-gray-900">
                  {group.title.replace('Player:', 'Flex:')}
                </div>
                <div
                  className={cn('text-xs', {
                    'text-gray-800 font-semibold': idx === selectedPrimaryIndex,
                    'text-gray-700': idx !== selectedPrimaryIndex,
                  })}
                >
                  {selectedPrice && idx === selectedPrimaryIndex
                    ? selectedPrice
                    : `$${group.price}/mo`}
                </div>
              </div>
            </Tab>
          ))}
        </Tab.List>
      </Tab.Group>
      <Tab.Group selectedIndex={selectedSecondaryIndex}>
        <Tab.List className="flex flex-col gap-4">
          {groupedTabs[selectedPrimaryIndex].tabs.map((tab, idx) => (
            <Tab
              key={tab.slug}
              as={LinkToSubscription}
              selected={idx === selectedSecondaryIndex}
              href={tab.slug}
              className={({ selected }) =>
                cn(
                  'w-full h-full rounded-lg py-4 px-3 cursor-pointer border focus-visible:outline-none hover:shadow-activeBorder hover:border-transparent dark:hover:border-transparent transition',
                  {
                    'shadow-activeBorder border-transparent': selected,
                    'border-nzxt-light-grey-300 dark:border-nzxt-dark-grey-100':
                      !selected,
                  }
                )
              }
            >
              <div className="flex text-left items-center w-full">
                <div className="w-full">
                  <div className="text-gray-600 text-sm float-right">
                    {tab.price}
                  </div>
                  <div className="font-semibold text-sm flex gap-2 items-center">
                    {tab.title} {tab.slug.includes('-prime') && <PrimeBadge />}{' '}
                    {tab.popular && <PopularBadge />}
                  </div>

                  <p className="text-gray-600 text-xs py-1">
                    {tab.description}
                  </p>
                  {idx === selectedSecondaryIndex && tab.specs && (
                    <div>
                      <div className="py-2">
                        <Divider />
                      </div>
                      <ul className="list-disc pl-5 mt-2 text-sm px-4">
                        {tab.specs.map(spec => (
                          <li key={spec} className="text-gray-600 py-0.5">
                            {spec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </Tab>
          ))}
        </Tab.List>
      </Tab.Group>
    </div>
  );
};

export default memo(PdpTierSelector);
